import { create } from 'zustand';
import type { LatLng } from '../lib/geo';
import type { NavFix } from '../lib/nav';
import type { PlanResult } from '../lib/route';
import { alphaFromSlider } from '../lib/route';
import type { HeatmapEdge, SweepPoint } from '../lib/sweep';
import type { PresetKey, WeatherForecast } from '../lib/weather';
import type { DataMeta, ProgressPhase } from '../workers/protocol';
import { requestHeatmap, requestPlan, requestSweep, SupersededError } from './workerClient';

export type Which = 'a' | 'b';
export type SheetSnap = 'peek' | 'half' | 'full';

export interface PointSel {
  ll: LatLng;
  label: string;
  /** true se l'etichetta è provvisoria (coordinate) e va migliorata col reverse geocoding */
  needsReverse?: boolean;
}

export interface UserPosition {
  ll: LatLng;
  accuracyM: number;
  heading: number | null;
  at: number;
}

export function coordLabel(ll: LatLng): string {
  return `${ll.lat.toFixed(5)}, ${ll.lng.toFixed(5)}`;
}

/** Valori dello slider corrispondenti ai preset manuali. */
export const PRESET_SLIDER: Record<Exclude<PresetKey, 'auto'>, number> = { fast: 0, max: 85 };
export const BALANCED_SLIDER = 40;

interface AppState {
  a: PointSel | null;
  b: PointSel | null;
  pickTarget: Which | null;
  departNow: boolean;
  departMs: number;
  mode: 'auto' | 'manual';
  sliderPct: number;
  status: 'idle' | 'loading' | 'done' | 'error';
  phase: ProgressPhase | null;
  error: string | null;
  hint: string | null;
  infoOpen: boolean;
  plan: PlanResult | null;
  meta: DataMeta | null;
  /** orario di partenza effettivamente usato per il piano corrente */
  planDepartMs: number | null;

  // meteo (informa, non decide)
  weather: WeatherForecast | null;
  weatherKey: string | null;

  // «quando partire?»
  sweep: SweepPoint[] | null;
  sweepStatus: 'idle' | 'loading' | 'error';

  // heatmap dell'ombra sulla mappa
  heatmapOn: boolean;
  heatmap: HeatmapEdge[] | null;
  heatmapForMs: number | null;
  heatmapLoading: boolean;

  // navigazione live
  navActive: boolean;
  navFix: NavFix | null;
  userPos: UserPosition | null;
  followUser: boolean;

  /** richiesta di snap per il bottom sheet (consumata dal componente Sheet) */
  sheetRequest: SheetSnap | null;

  setPoint: (which: Which, ll: LatLng, label?: string) => void;
  setBoth: (a: LatLng, b: LatLng, labelA?: string, labelB?: string) => void;
  setPointLabel: (which: Which, label: string) => void;
  clearPoint: (which: Which) => void;
  swap: () => void;
  setPickTarget: (t: Which | null) => void;
  handleMapClick: (ll: LatLng) => void;
  setDepartNow: (now: boolean) => void;
  setDepartMs: (ms: number) => void;
  setMode: (mode: 'auto' | 'manual') => void;
  setSlider: (pct: number) => void;
  applyAlpha: (alpha: number) => void;
  applyPreset: (preset: PresetKey | 'balanced') => void;
  setHint: (hint: string | null) => void;
  setInfoOpen: (open: boolean) => void;
  compute: () => Promise<void>;

  setWeather: (forecast: WeatherForecast | null, key: string | null) => void;
  runSweep: () => Promise<void>;
  toggleHeatmap: () => void;
  refreshHeatmap: () => Promise<void>;

  startNav: () => void;
  stopNav: () => void;
  setUserPos: (pos: UserPosition | null) => void;
  setNavFix: (fix: NavFix | null) => void;
  setFollowUser: (follow: boolean) => void;
  requestSheet: (snap: SheetSnap | null) => void;
}

export const useAppStore = create<AppState>()((set, get) => ({
  a: null,
  b: null,
  pickTarget: null,
  departNow: true,
  departMs: Date.now(),
  mode: 'auto',
  sliderPct: 60,
  status: 'idle',
  phase: null,
  error: null,
  hint: null,
  infoOpen: false,
  plan: null,
  meta: null,
  planDepartMs: null,

  weather: null,
  weatherKey: null,
  sweep: null,
  sweepStatus: 'idle',
  heatmapOn: false,
  heatmap: null,
  heatmapForMs: null,
  heatmapLoading: false,
  navActive: false,
  navFix: null,
  userPos: null,
  followUser: false,
  sheetRequest: null,

  setPoint: (which, ll, label) =>
    set({
      [which]: { ll, label: label ?? coordLabel(ll), needsReverse: !label },
    } as Partial<AppState>),

  setBoth: (a, b, labelA, labelB) =>
    set({
      a: { ll: a, label: labelA ?? coordLabel(a), needsReverse: !labelA },
      b: { ll: b, label: labelB ?? coordLabel(b), needsReverse: !labelB },
    }),

  setPointLabel: (which, label) => {
    const p = get()[which];
    if (p) set({ [which]: { ...p, label, needsReverse: false } } as Partial<AppState>);
  },

  clearPoint: (which) =>
    set({
      [which]: null,
      plan: null,
      meta: null,
      status: 'idle',
      error: null,
      sweep: null,
      heatmap: null,
      navActive: false,
      navFix: null,
    } as Partial<AppState>),

  swap: () => {
    const { a, b } = get();
    set({ a: b, b: a });
  },

  setPickTarget: (t) => set({ pickTarget: t, sheetRequest: t ? 'peek' : get().sheetRequest }),

  handleMapClick: (ll) => {
    const { pickTarget, a, b, setPoint } = get();
    if (pickTarget) {
      setPoint(pickTarget, ll);
      set({ pickTarget: null });
      return;
    }
    if (!a) {
      setPoint('a', ll);
      return;
    }
    if (!b) {
      setPoint('b', ll);
      return;
    }
    set({
      hint: 'Per spostare un punto trascina il marker, oppure tocca il mirino accanto al campo.',
    });
  },

  setDepartNow: (now) => set({ departNow: now, departMs: now ? Date.now() : get().departMs }),
  setDepartMs: (ms) => set({ departMs: ms, departNow: false }),
  setMode: (mode) => set({ mode }),
  setSlider: (pct) => set({ sliderPct: pct, mode: 'manual' }),

  applyAlpha: (alpha) => {
    // inversa di alphaFromSlider: p = 100·(α/8)^(1/1.6)
    const pct = Math.round(100 * Math.pow(Math.max(0, alpha) / 8, 1 / 1.6));
    set({ mode: 'manual', sliderPct: pct });
  },

  applyPreset: (preset) => {
    if (preset === 'auto') set({ mode: 'auto' });
    else if (preset === 'balanced') set({ mode: 'manual', sliderPct: BALANCED_SLIDER });
    else set({ mode: 'manual', sliderPct: PRESET_SLIDER[preset] });
  },

  setHint: (hint) => set({ hint }),
  setInfoOpen: (open) => set({ infoOpen: open }),

  compute: async () => {
    const { a, b, departNow, departMs, mode, sliderPct } = get();
    if (!a || !b) return;
    const depart = departNow ? Date.now() : departMs;
    set({ status: 'loading', error: null, phase: null });
    const { promise } = requestPlan(
      {
        a: a.ll,
        b: b.ll,
        departMs: depart,
        mode: mode === 'auto' ? 'auto' : { alpha: alphaFromSlider(sliderPct) },
      },
      (phase: ProgressPhase) => set({ phase }),
    );
    try {
      const { plan, meta } = await promise;
      const first = get().plan === null;
      set({
        status: 'done',
        plan,
        meta,
        planDepartMs: depart,
        phase: null,
        sweep: null,
        sweepStatus: 'idle',
        sheetRequest: first ? 'half' : get().sheetRequest,
      });
      if (get().heatmapOn) void get().refreshHeatmap();
    } catch (err) {
      if (err instanceof SupersededError) return;
      set({
        status: 'error',
        error: err instanceof Error ? err.message : String(err),
        phase: null,
        plan: null,
        meta: null,
        sweep: null,
      });
    }
  },

  setWeather: (forecast, key) => set({ weather: forecast, weatherKey: key }),

  runSweep: async () => {
    const { plan, planDepartMs } = get();
    if (!plan || planDepartMs === null) return;
    set({ sweepStatus: 'loading' });
    try {
      const points = await requestSweep({
        segments: plan.chosen.segments.map((s) => ({
          coords: s.coords,
          lengthM: s.lengthM,
          covered: s.covered,
          isSteps: s.isSteps,
        })),
        fromMs: planDepartMs,
        stepMs: 30 * 60_000,
        count: 13,
      });
      set({ sweep: points, sweepStatus: 'idle' });
    } catch {
      set({ sweepStatus: 'error' });
    }
  },

  toggleHeatmap: () => {
    const on = !get().heatmapOn;
    set({ heatmapOn: on });
    if (on) void get().refreshHeatmap();
  },

  refreshHeatmap: async () => {
    const { plan, planDepartMs } = get();
    if (!plan) return;
    const t = planDepartMs ?? Date.now();
    set({ heatmapLoading: true });
    try {
      const edges = await requestHeatmap(t);
      set({ heatmap: edges, heatmapForMs: t, heatmapLoading: false });
    } catch (err) {
      set({
        heatmapLoading: false,
        heatmapOn: false,
        hint: err instanceof Error ? err.message : 'Heatmap non disponibile.',
      });
    }
  },

  startNav: () => set({ navActive: true, followUser: true, navFix: null, sheetRequest: 'peek' }),
  stopNav: () => set({ navActive: false, navFix: null, followUser: false, sheetRequest: 'half' }),
  setUserPos: (pos) => set({ userPos: pos }),
  setNavFix: (fix) => set({ navFix: fix }),
  setFollowUser: (follow) => set({ followUser: follow }),
  requestSheet: (snap) => set({ sheetRequest: snap }),
}));

export type { DataMeta, PlanResult };
